import { parse } from 'papaparse';
import levenshtein from 'js-levenshtein'; // npm install papaparse js-levenshtein

// Compile cleanly in React 16.4.2 (no optional chaining)
// Detect “Date of Birth” → DOB
// Handle aliases + fuzzy matches
// Reject only truly missing required columns

// --- Define schema with isOptional flag ---
const rosterSchema = [
  { name: 'STUDENT-ID', isOptional: false },
  { name: 'Local ID', isOptional: false },
  { name: 'TSDS Unique ID', isOptional: false },
  { name: 'First-Name', isOptional: false },
  { name: 'Middle-Name', isOptional: false },
  { name: 'Ethnicity', isOptional: false },
  { name: 'Last-Name', isOptional: false },
  { name: 'DOB', isOptional: false },
  { name: 'Grade-Level', isOptional: false },
  { name: 'Campus-ID', isOptional: false },

  // Optional ones
  { name: 'Sex', isOptional: true },
  { name: 'EcoDis', isOptional: true },
  { name: 'SpEd', isOptional: true },
  { name: 'LEP', isOptional: true },
  { name: 'Enrollment', isOptional: true },
  { name: 'Snapshot', isOptional: true },
  { name: 'Years in US Schools', isOptional: true },
  { name: 'Asylee,Refugee,SIFE', isOptional: true },
  { name: 'Test 1', isOptional: true },
  { name: 'Test 1 Teacher ID or Name', isOptional: true },
  { name: 'Test 2', isOptional: true },
  { name: 'Test 2 Teacher ID or Name', isOptional: true },
  { name: 'Test 3', isOptional: true },
  { name: 'Test 3 Teacher ID or Name', isOptional: true },
  { name: 'Test 4', isOptional: true },
  { name: 'Test 4 Teacher ID or Name', isOptional: true },
  { name: 'Test 5', isOptional: true },
  { name: 'Test 5 Teacher ID or Name', isOptional: true },
  { name: 'Tags', isOptional: true },
  { name: 'Highly Mobile', isOptional: true },
  { name: 'Monitor', isOptional: true },
  { name: 'Mentor', isOptional: true },
];

// --- Alias dictionary for semantic matching ---
const aliasMap = {
  DOB: ['dateofbirth', 'date_of_birth', 'birthdate', 'birth_date', 'birthofdate', 'datebirth', 'dateofbith', 'birth'],
  'First-Name': ['firstname', 'first_name', 'givenname', 'first'],
  'Last-Name': ['lastname', 'surname', 'familyname', 'last'],
  'Grade-Level': ['grade', 'classlevel', 'yearlevel', 'gradelevel'],
  'Campus-ID': ['campusid', 'schoolid', 'campuscode', 'schoolcode'],
  'STUDENT-ID': ['studentid', 'studentnumber', 'id'],
  'Test 1 Teacher ID or Name': ['test1teachername', 'test1teacherid', 'test1teacher'],
  'Test 2 Teacher ID or Name': ['test2teachername', 'test2teacherid', 'test2teacher'],
  'Test 3 Teacher ID or Name': ['test3teachername', 'test3teacherid', 'test3teacher'],
  'Test 4 Teacher ID or Name': ['test4teachername', 'test4teacherid', 'test4teacher'],
  'Test 5 Teacher ID or Name': ['test5teachername', 'test5teacherid', 'test5teacher'],
};

// --- Normalize header for fuzzy/alias matching ---
function normalize(str) {
  if (!str) return '';
  return String(str)
    .toLowerCase()
    .replace(/[\s\-_]/g, '');
}

// --- Find closest match using fuzzy + alias map ---
function findClosestMatch(target, headers, schemaName) {
  const aliases = aliasMap[schemaName] || [];
  const normalizedAliases = aliases.map(normalize);

  // Exact or alias match first
  for (let i = 0; i < normalizedAliases.length; i++) {
    const alias = normalizedAliases[i];
    const exact = headers.find((h) => h.normalized === alias);
    if (exact) {
      return {
        name: exact.normalized,
        original: exact.original,
        distance: 0,
        type: 'alias',
      };
    }
  }

  // // Otherwise use fuzzy matching
  // let best = { name: null, original: null, distance: Infinity, type: 'fuzzy' };
  // headers.forEach((h) => {
  //   const distance = levenshtein(target, h.normalized);
  //   if (distance < best.distance) {
  //     best = { name: h.normalized, original: h.original, distance, type: 'fuzzy' };
  //   }
  // });
  // return best;
  return null;
}

// --- Main validation function ---
export const validateStudentRoster = (file) =>
  new Promise((resolve, reject) => {
    if (!file || file.type !== 'text/csv') {
      return reject(new Error('Invalid file type. Please upload a CSV file.'));
    }

    const reader = new FileReader();

    reader.onload = function() {
      const text = reader.result;

      const result = parse(text, { skipEmptyLines: true });

      if (result.errors && result.errors.length) {
        return reject(new Error("Couldn't parse CSV file."));
      }

      const headers = result.data && result.data[0];
      if (!headers || headers.length === 0) {
        return reject(new Error('Empty or missing header row.'));
      }

      // Build normalized header array (for fuzzy/alias match)
      const normalizedHeaders = headers.map((h) => ({
        original: h,
        normalized: normalize(h),
      }));

      const missing = [];
      const suggestions = [];

      rosterSchema.forEach(function(col) {
        const name = col.name;
        const norm = normalize(name);
        const bestMatch = findClosestMatch(norm, normalizedHeaders, name);

        // Consider a match good if distance <= 3 or alias found
        if (bestMatch) {
          if (bestMatch.distance > 3 && bestMatch.type !== 'alias') {
            if (!col.isOptional) missing.push(name);
          } else {
            // Record fuzzy/alias suggestions if header name differs
            if (bestMatch.original && normalize(bestMatch.original) !== norm) {
              suggestions.push(
                'Expected "' + name + '" → matched with "' + bestMatch.original + '" (' + bestMatch.type + ')'
              );
            }
          }
        }
      });

      if (missing.length > 0) {
        return reject(new Error('Missing required columns:\n' + missing.join(', ')));
      }

      resolve({
        valid: true,
        suggestions,
      });
    };

    reader.readAsText(file);
  });

// --- Export default validator set ---
const StudentsValidators = {
  validateStudentRoster,
};

export default StudentsValidators;
